import * as React from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { fetchPatientDashboard } from "../../Services/apiService";
import { useTranslation } from "../../i18n/LanguageContext";
import { C, cardSx } from "../../theme/dashboardCardTokens";
import StatCard from "../../components/portal/StatCard";
import StatCardDark from "../../components/portal/StatCardDark";
import PortalCardHeader from "../../components/portal/PortalCardHeader";
import PortalPageHeader from "../../components/portal/PortalPageHeader";

dayjs.extend(relativeTime);

const iconSx = { fontSize: 15, color: C.primary };
const iconSxDark = { fontSize: 15, color: "#fff" };

const SUMMARY_CARDS = [
  { key: "total_orders", labelKey: "totalOrders", Icon: Inventory2OutlinedIcon },
  { key: "in_progress", labelKey: "inProgress", Icon: PendingActionsOutlinedIcon },
  { key: "completed", labelKey: "completed", Icon: CheckCircleOutlineIcon },
  { key: "reports_available", labelKey: "reportsAvailable", Icon: DescriptionOutlinedIcon },
];

// Patient portal home — summary counts + the last 5 status changes across
// all of this patient's tests. The per-order/per-test detail lives on
// My Orders; the report list lives on My Reports. Card styling mirrors the
// admin dashboard's bento cards (StatCardLight/CardHeader/ActivityRow in
// pages/admin/Dashboard.js) via the shared tokens in theme/dashboardCardTokens.
function PatientDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    fetchPatientDashboard()
      .then((res) => {
        if (!cancelled) setData(res.data || {});
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) {
          navigate("/dashboard", { replace: true });
          return;
        }
        setError(err.message || t("portal.dashboard.patient.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ color: C.ink }} />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  const recentActivity = data?.recent_activity || [];

  return (
    <Box>
      <PortalPageHeader
        title={t("portal.dashboard.patient.pageTitle")}
        subtitle={t("portal.dashboard.patient.pageSubtitle")}
      />

      <Box sx={{ display: "flex", gap: "20px", flexWrap: "wrap", mb: "20px" }}>
        {SUMMARY_CARDS.map((card, idx) =>
          idx === 0 ? (
            <StatCardDark
              key={card.key}
              icon={<card.Icon sx={iconSxDark} />}
              value={data?.[card.key] ?? 0}
              label={t(`portal.dashboard.patient.summary.${card.labelKey}`)}
            />
          ) : (
            <StatCard
              key={card.key}
              icon={<card.Icon sx={iconSx} />}
              value={data?.[card.key] ?? 0}
              label={t(`portal.dashboard.patient.summary.${card.labelKey}`)}
            />
          )
        )}
      </Box>

      {data?.reports_available > 0 && (
        <Box
          onClick={() => navigate("/patient/reports")}
          sx={{
            ...cardSx,
            mb: "20px",
            p: "18px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            background: C.tintBg,
            border: "none",
            cursor: "pointer",
            "&:hover": { opacity: 0.9 },
          }}
        >
          <Typography sx={{ fontSize: 14, color: C.primary, fontWeight: 700 }}>
            {t(`portal.dashboard.patient.reportsReady.${data.reports_available === 1 ? "one" : "other"}`, {
              count: data.reports_available,
            })}
          </Typography>
          <Box component="span" sx={{ fontSize: 13, fontWeight: 700, color: C.primary }}>
            {t("portal.dashboard.patient.goToReports")} →
          </Box>
        </Box>
      )}

      <Box sx={{ ...cardSx, p: "26px 28px" }}>
        <PortalCardHeader
          title={t("portal.dashboard.recentActivity.title")}
          subtitle={t("portal.dashboard.recentActivity.subtitle")}
        />

        {recentActivity.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: C.textFaint, textAlign: "center", py: 3 }}>
            {t("portal.dashboard.recentActivity.empty")}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {recentActivity.map((activity, idx) => (
              <Box
                key={`${activity.lab_test_id}-${activity.changed_at}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  p: "14px",
                  borderRadius: "14px",
                  background: idx % 2 === 0 ? "#FAFAFA" : "#FFFFFF",
                }}
              >
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: C.gray2, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 600, lineHeight: 1.4 }}>
                    {activity.substrate} · {activity.test_identifier}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: "5px", flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 12, color: C.textFaint }}>
                      {activity.changed_at ? dayjs(activity.changed_at).fromNow() : "—"}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.primary,
                        background: C.tintBg,
                        px: "9px",
                        py: "3px",
                        borderRadius: "999px",
                      }}
                    >
                      {t(`portal.statusTimeline.steps.${activity.step_key}.label`)}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PatientDashboard;
